import { useState, useEffect } from "react";
import { CalendarPlus, CalendarX, Clock, Video, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { leadsApi } from "@/lib/crmApi";

/**
 * "Fixer RDV" / "Modifier RDV" button.
 *
 * Opens a dialog with a precise date+time picker (browser-native) and a
 * physique/visio toggle. Persists by patching the lead with `rdv_at` (ISO
 * UTC) and `rdv_kind`. The Calendar page picks up the change automatically
 * because it requeries on focus.
 *
 * `rdv_at` in DB is stored as ISO UTC. We expose Africa/Dakar local time in
 * the picker for the user's convenience and convert on save / on load.
 */

// Africa/Dakar is UTC+0 year-round (no DST), so iso ↔ local is a no-op for
// us. We still go through a proper conversion in case the user travels.
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    // <input type="datetime-local"> wants `YYYY-MM-DDTHH:mm` in local TZ
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes())
    );
  } catch {
    return "";
  }
}

function toIsoUtc(localValue) {
  if (!localValue) return null;
  // datetime-local has no timezone — interpret as local, convert to UTC ISO
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatNice(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function RdvSetterButton({ lead, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [datetime, setDatetime] = useState("");
  const [kind, setKind] = useState("physique");
  const [saving, setSaving] = useState(false);
  // Tracks whether the user already tried to submit. Used to show inline
  // validation feedback (red border + helper text) only after the first
  // attempt — pre-filled forms shouldn't shout "required" unprompted.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Reset form whenever the dialog opens (so previous edits don't linger)
  useEffect(() => {
    if (open) {
      setDatetime(toLocalInput(lead?.rdv_at));
      setKind(lead?.rdv_kind || "physique");
      setSubmitAttempted(false);
    }
  }, [open, lead?.rdv_at, lead?.rdv_kind]);

  const hasRdv = !!lead?.rdv_at;
  const datetimeMissing = !datetime;

  const save = async () => {
    setSubmitAttempted(true);
    if (!datetime) {
      toast.error("Choisis une date et une heure pour le RDV");
      return;
    }
    const isoUtc = toIsoUtc(datetime);
    if (!isoUtc) {
      toast.error("Date invalide");
      return;
    }
    // Refuse RDVs in the past — common typo (forgot to change the year)
    if (new Date(isoUtc).getTime() < Date.now() - 60 * 60 * 1000) {
      if (!window.confirm("Cette date est dans le passé. Continuer ?")) return;
    }
    setSaving(true);
    try {
      await leadsApi.update(lead.id, {
        rdv_at: isoUtc,
        rdv_kind: kind,
        // Bumping the conversation status keeps the dashboard / inbox in sync
        // with the "rdv_confirme" badge so it shows up in the calendar buckets.
        conversation_status: "rdv_confirme",
      });
      toast.success(`RDV fixé : ${formatNice(isoUtc)}`);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const clearRdv = async () => {
    if (!window.confirm("Supprimer ce RDV ?")) return;
    setSaving(true);
    try {
      await leadsApi.update(lead.id, {
        rdv_at: null,
        rdv_kind: "",
        conversation_status: "nouveau",
      });
      toast.success("RDV supprimé");
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="gap-1.5"
        data-testid="rdv-setter-btn"
      >
        <CalendarPlus className="h-3.5 w-3.5" />
        {hasRdv ? "Modifier RDV" : "Fixer RDV"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {hasRdv ? "Modifier le rendez-vous" : "Planifier un rendez-vous"}
            </DialogTitle>
            <DialogDescription>
              Choisis la date et l'heure exactes (heure Dakar). Le calendrier et
              les rappels s'adaptent automatiquement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {hasRdv && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm dark:border-emerald-900/40 dark:bg-emerald-950/30">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  RDV actuel
                </p>
                <p className="mt-0.5 font-medium">{formatNice(lead.rdv_at)}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="rdv-datetime" className="flex items-center gap-1.5 text-xs">
                <Clock className="h-3 w-3" />
                Date et heure
              </Label>
              <Input
                id="rdv-datetime"
                type="datetime-local"
                value={datetime}
                onChange={(e) => setDatetime(e.target.value)}
                className={
                  "font-mono " +
                  (submitAttempted && datetimeMissing
                    ? "border-red-400 focus-visible:ring-red-300"
                    : "")
                }
                data-testid="rdv-datetime-input"
                step="900" // 15-minute granularity
                aria-invalid={submitAttempted && datetimeMissing}
                aria-describedby="rdv-datetime-help"
              />
              {submitAttempted && datetimeMissing ? (
                <p
                  id="rdv-datetime-help"
                  className="text-[11px] font-medium text-red-600"
                  data-testid="rdv-datetime-error"
                >
                  Date et heure obligatoires.
                </p>
              ) : (
                <p
                  id="rdv-datetime-help"
                  className="text-[10px] text-muted-foreground"
                >
                  Heure locale (Africa/Dakar). Précision : 15 minutes.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Type de RDV</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="rdv-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="physique">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5" />
                      Physique (visite / agence)
                    </span>
                  </SelectItem>
                  <SelectItem value="visio">
                    <span className="inline-flex items-center gap-2">
                      <Video className="h-3.5 w-3.5" />
                      Visio (Zoom / WhatsApp)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {datetime && (
              <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Aperçu
                </p>
                <p className="mt-0.5 text-sm font-medium">
                  {formatNice(toIsoUtc(datetime))}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {hasRdv && (
              <Button
                variant="ghost"
                onClick={clearRdv}
                disabled={saving}
                className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                data-testid="rdv-clear-btn"
              >
                <CalendarX className="h-4 w-4 mr-1.5" />
                Supprimer
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Annuler
              </Button>
              {/* Button stays clickable so the toast + inline message fire
                  even when the field is empty. The save() handler validates. */}
              <Button
                onClick={save}
                disabled={saving}
                data-testid="rdv-save-btn"
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
